import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, NgZone, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';

export enum SliderState {
  IDLE = 'idle',
  LIKE = 'like',
  SUPERLIKE = 'superlike',
  BLOCK = 'block'
}

@Component({
  selector: 'app-slider-control',
  templateUrl: './slider-control.component.html',
  styleUrls: ['./slider-control.component.scss'],
})
export class SliderControlComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  @Input() initialState: SliderState = SliderState.IDLE;
  @Input() enableHide = false; // New input to control hide functionality
  @Input() logoIconSrc?: string; // Optional input to customize the logo
  @Output() stateChange = new EventEmitter<SliderState>();
  @Output() dragStart = new EventEmitter<void>();
  @Output() dragEnd = new EventEmitter<void>();
  @Output() hideTriggered = new EventEmitter<void>();
  @Output() dragProgress = new EventEmitter<{direction: 'left' | 'right' | 'down' | null, progress: number}>();

  @ViewChild('sliderBar') sliderBarRef!: ElementRef<HTMLElement>;
  @ViewChild('sliderHandle') sliderHandleRef!: ElementRef<HTMLElement>;

  public actionIconOpacity = 0;
  public readonly defaultLogoIconSrc = "/assets/imgs/logo-slider.svg";
  public readonly likeIconSrc = "/assets/imgs/likegreenbg2.svg";
  public readonly superlikeIconSrc = "/assets/imgs/superlike.svg";

  public SliderState = SliderState;
  public currentState: SliderState = SliderState.IDLE;
  public isHolding = false;
  public visualDirection: 'left' | 'right' | null = null;
  public translateX = 0;
  public translateY = 0;

  public stop1_pos = 0;
  public stop2_pos = 50;
  public stop3_pos = 100;
  
  // As posições são calculadas dinamicamente após a view ser renderizada.
  private LIKE_POSITION = 120;
  private SUPERLIKE_POSITION = -120;
  private HIDE_THRESHOLD = 40; // Distance to trigger hide

  private startX = 0;
  private startY = 0;
  private readonly MOVE_THRESHOLD = 120; // Para movimento vertical (baixo)
  private readonly LATERAL_MOVE_THRESHOLD = 150; // Para movimento lateral (esquerda/direita) - aumenta exigência no iOS
  private readonly DRAG_LIMIT_OFFSET = 30; // Px extras que o usuário pode arrastar além do limite.
  private readonly CENTER_TOLERANCE = 12;
  private commitDirection: 'left' | 'right' | 'down' | null = null;
  private hasHorizontalIntent = false; // Flag para bloquear arraste vertical quando há intenção horizontal
  private verticalDragStartXAdjusted = false; // Flag para rastrear se já ajustamos startX para arraste vertical

  private onDragFn = this.onDrag.bind(this);
  private onHoldEndFn = this.onHoldEnd.bind(this);
  private viewInitialized = false; // Flag para rastrear se ngAfterViewInit já executou

  constructor(private zone: NgZone, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    // Garante que o estado inicial seja sempre aplicado
    // Se initialState não estiver definido, mantém IDLE como padrão
    // Mas não aplica ainda pois as posições ainda não foram calculadas
    // O estado será aplicado no ngAfterViewInit após calcular as posições
    this.currentState = this.initialState || SliderState.IDLE;
  }

  /**
   * Retorna a logo a ser usada. Se logoIconSrc foi fornecido via @Input, usa ele, senão usa a padrão.
   */
  get displayLogoIconSrc(): string {
    return this.logoIconSrc || this.defaultLogoIconSrc;
  }

  ngAfterViewInit(): void {
    // Adia o cálculo para garantir que as dimensões do DOM estejam disponíveis.
    // Usa requestAnimationFrame para garantir que o layout foi calculado
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.calculatePositions();
        this.viewInitialized = true; // Marca que a view foi inicializada
        
        // Após calcular as posições, aplica o estado inicial com as posições corretas
        // Sempre usa o initialState mais recente para garantir que está correto
        const stateToApply = this.initialState || this.currentState || SliderState.IDLE;
        this.currentState = stateToApply; // Atualiza o currentState
        
        // Aplica o estado múltiplas vezes com delays para garantir que funcione
        const applyState = () => {
          if (this.initialState !== undefined && this.initialState !== null) {
            this.setState(this.initialState, false);
          } else {
            this.setState(stateToApply, false);
          }
        };
        
        // Aplica imediatamente
        applyState();
        
        // Aplica novamente após um frame
        requestAnimationFrame(() => {
          applyState();
        });
        
        // Aplica com delays progressivos para garantir que funcione
        setTimeout(() => {
          applyState();
        }, 50);
        
        setTimeout(() => {
          applyState();
        }, 100);
        
        setTimeout(() => {
          applyState();
        }, 200);
        
        setTimeout(() => {
          applyState();
        }, 300);
      });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialState']) {
      const newState = changes['initialState'].currentValue;
      
      // Sempre aplica o novo estado, independente se é diferente ou não
      // Isso é crítico quando componentes são reciclados durante scroll
      // e podem ter estado interno incorreto mesmo com initialState correto
      if (newState !== undefined && newState !== null) {
        this.currentState = newState; // Atualiza o currentState
        
        // Função auxiliar para aplicar o estado múltiplas vezes
        const applyStateMultiple = () => {
          if (this.sliderBarRef?.nativeElement && this.sliderHandleRef?.nativeElement) {
            // Garante que as posições foram calculadas
            if (this.LIKE_POSITION === 120 && this.SUPERLIKE_POSITION === -120) {
              this.calculatePositions();
            }
            this.setState(newState, false);
          }
        };
        
        // Se a view já foi inicializada e as posições foram calculadas, aplica imediatamente
        if (this.viewInitialized && (this.LIKE_POSITION !== 120 || this.SUPERLIKE_POSITION !== -120)) {
          // Posições já calculadas, aplica múltiplas vezes para garantir
          applyStateMultiple();
          
          // Aplica novamente após um frame
          requestAnimationFrame(() => {
            applyStateMultiple();
          });
          
          // Aplica com delays progressivos
          setTimeout(() => {
            applyStateMultiple();
          }, 50);
          
          setTimeout(() => {
            applyStateMultiple();
          }, 100);
          
          setTimeout(() => {
            applyStateMultiple();
          }, 200);
        } else if (this.viewInitialized) {
          // View inicializada mas posições ainda não calculadas (raro, mas possível)
          // Força o cálculo e aplica múltiplas vezes
          applyStateMultiple();
          
          setTimeout(() => {
            applyStateMultiple();
          }, 50);
          
          setTimeout(() => {
            applyStateMultiple();
          }, 100);
        } else {
          // View ainda não inicializada, aguarda que o ngAfterViewInit calcule
          // O ngAfterViewInit aplicará o estado automaticamente após calcular as posições
          // Mas também aguardamos aqui para garantir que funcione se o ngAfterViewInit já executou
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              // Verifica se a view foi inicializada e as posições foram calculadas
              if (this.viewInitialized && (this.LIKE_POSITION !== 120 || this.SUPERLIKE_POSITION !== -120)) {
                // Posições já foram calculadas pelo ngAfterViewInit
                this.setState(newState, false);
              } else if (this.sliderBarRef?.nativeElement && this.sliderHandleRef?.nativeElement) {
                // Se os elementos estão disponíveis mas as posições ainda não foram calculadas
                // Força o cálculo e aplica
                this.calculatePositions();
                this.viewInitialized = true; // Marca como inicializado
                this.setState(newState, false);
              } else {
                // Se os elementos ainda não estão disponíveis, aguarda mais um pouco
                setTimeout(() => {
                  if (this.sliderBarRef?.nativeElement && this.sliderHandleRef?.nativeElement) {
                    this.calculatePositions();
                    this.viewInitialized = true; // Marca como inicializado
                    this.setState(newState, false);
                  }
                }, 50);
              }
            });
          });
        }
      }
    }
  }

  ngOnDestroy(): void {
    this.removeEventListeners();
  }

  /**
   * Calcula as posições de 'like' e 'superlike' com base na largura
   * da barra e do manipulador, tornando o componente responsivo.
   */
  private calculatePositions(): void {
    const barWidth = this.sliderBarRef.nativeElement.offsetWidth;
    const handleWidth = this.sliderHandleRef.nativeElement.offsetWidth;
    const maxTravel = (barWidth - handleWidth) / 2;

    this.LIKE_POSITION = maxTravel;
    this.SUPERLIKE_POSITION = -maxTravel;
  }

  /**
   * Verifica se o slider está em um estado que impede interação.
   */
  private isLocked(): boolean {
    return this.currentState === SliderState.BLOCK || this.currentState === SliderState.SUPERLIKE;
  }

  /**
   * Alterna o estado do componente ao ser clicado. Se o estado selecionado
   * já estiver ativo, ele retorna para IDLE.
   * @param newState O estado para o qual o slider deve mudar.
   */
  public selectState(newState: SliderState): void {
    if (this.isLocked()) {
      return;
    }

    if (this.currentState === newState) {
      if (newState === SliderState.LIKE) {
          return;
      }
      this.setState(SliderState.IDLE);
    } else {
      // Anima a bolinha suavemente até a posição final
      this.animateToState(newState);
    }
  }

  /**
   * Anima a bolinha suavemente até a posição final do estado selecionado
   * @param newState O estado para o qual o slider deve animar
   */
  private animateToState(newState: SliderState): void {
    const startX = this.translateX;
    let targetX: number;
    let targetOpacity: number;
    let targetDirection: 'left' | 'right' | null;
    let targetStop1: number, targetStop2: number, targetStop3: number;

    switch (newState) {
      case SliderState.SUPERLIKE:
        targetX = this.SUPERLIKE_POSITION;
        targetOpacity = 1;
        targetDirection = 'left';
        targetStop1 = 0; targetStop2 = 90; targetStop3 = 140;
        break;
      case SliderState.LIKE:
        targetX = this.LIKE_POSITION;
        targetOpacity = 1;
        targetDirection = 'right';
        targetStop1 = -40; targetStop2 = 10; targetStop3 = 100;
        break;
      default: // IDLE
        targetX = 0;
        targetOpacity = 0;
        targetDirection = null;
        targetStop1 = 0; targetStop2 = 50; targetStop3 = 100;
        break;
    }

    const duration = 300; // duração da animação em ms
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      this.zone.run(() => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Easing function (ease-out)
        const easeOut = 1 - Math.pow(1 - progress, 3);

        // Interpola os valores
        this.translateX = startX + (targetX - startX) * easeOut;
        this.actionIconOpacity = targetOpacity * easeOut;
        this.visualDirection = targetDirection;
        this.stop1_pos = targetStop1;
        this.stop2_pos = targetStop2;
        this.stop3_pos = targetStop3;

        // Emite o progresso do drag para sincronizar a animação da foto
        if (targetDirection) {
          const maxDistance = targetDirection === 'right' ? this.LIKE_POSITION : Math.abs(this.SUPERLIKE_POSITION);
          const currentDistance = Math.abs(this.translateX);
          const dragProgress = Math.min(currentDistance / maxDistance, 1);
          this.dragProgress.emit({ direction: targetDirection, progress: dragProgress });
        }

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          // Garante que chegou exatamente na posição final
          this.translateX = targetX;
          this.actionIconOpacity = targetOpacity;
          this.currentState = newState;
          this.stateChange.emit(this.currentState);
          // Emite progresso final
          if (targetDirection) {
            this.dragProgress.emit({ direction: targetDirection, progress: 1 });
          }
        }
      });
    };

    requestAnimationFrame(animate);
  }

  /**
   * Inicia a sequência de arrastar. Captura o evento do ponteiro e
   * registra a posição inicial.
   * @param event O evento de ponteiro (ex: clique ou toque).
   */
  public onHoldStart(event: PointerEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.dragStart.emit();
    if (this.isLocked()) {
      return;
    }

    event.preventDefault();
    this.isHolding = true;
    this.hasHorizontalIntent = false; // Reset da flag ao iniciar novo arraste
    this.verticalDragStartXAdjusted = false; // Reset da flag ao iniciar novo arraste
    (event.target as HTMLElement).setPointerCapture(event.pointerId);
    this.startX = event.clientX - this.translateX;
    this.startY = event.clientY - this.translateY;

    document.addEventListener('pointermove', this.onDragFn);
    document.addEventListener('pointerup', this.onHoldEndFn);
    document.addEventListener('pointercancel', this.onHoldEndFn);
  }

  /**
   * Manipula o movimento do ponteiro durante o arraste, atualizando a posição
   * do manipulador (translateX), a opacidade do ícone e o gradiente de fundo.
   * @param event O evento de movimento do ponteiro.
   */
  private onDrag(event: PointerEvent): void {
    this.zone.run(() => {
      if (!this.isHolding) return;

      const currentX = event.clientX;
      const currentY = event.clientY;
      const dx = currentX - this.startX;
      const dy = currentY - this.startY;

      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);

      // Determine if this is primarily a horizontal or vertical drag
      // Se já há intenção horizontal estabelecida, prioriza ela
      // Se não, estabelece intenção baseado no movimento atual
      const nearCenterX = absDx <= this.CENTER_TOLERANCE;
      const isHorizontalDrag = this.hasHorizontalIntent || (absDx > absDy && absDx > this.CENTER_TOLERANCE);
      const isVerticalDrag = !this.hasHorizontalIntent && nearCenterX && absDy > absDx && dy > 0 && this.enableHide; // Only downward, from center, and when hide is enabled

      if (isHorizontalDrag) {
        // Estabelece intenção horizontal se ainda não foi estabelecida
        if (!this.hasHorizontalIntent && absDx > this.CENTER_TOLERANCE) {
          this.hasHorizontalIntent = true;
        }
        // Horizontal drag for like/superlike
        // Allow full movement without extra limit on iOS/Android
        this.translateX = Math.max(this.SUPERLIKE_POSITION, Math.min(dx, this.LIKE_POSITION));
        this.translateY = 0; // Garante que não há movimento vertical durante arraste horizontal

        if (this.LIKE_POSITION > 0) {
          const travelPercent = Math.min(Math.abs(this.translateX) / this.LIKE_POSITION, 1);
          this.actionIconOpacity = travelPercent;

          // Atualiza o gradiente de fundo dinamicamente com base no progresso.
          const progress = this.translateX / this.LIKE_POSITION; // Varia de -1 a 1.
          const flex = 40; // Fator de flexão da cor central do gradiente.

          this.stop2_pos = 50 - (progress * flex);
          this.stop1_pos = Math.min(0, 0 - (progress * flex));
          this.stop3_pos = Math.max(100, 100 - (progress * flex));
        }

        // Determina a direção visual para mostrar o ícone correto.
        if (dx > 5) this.visualDirection = 'right';
        else if (dx < -5) this.visualDirection = 'left';
        else this.visualDirection = null;

        // Determina se o arraste ultrapassou o limiar para confirmar a ação.
        // Usa porcentagem do movimento total e threshold específico para movimento lateral (maior exigência no iOS)
        const likeThreshold = this.LIKE_POSITION * 0.8; // 80% do movimento lateral (aumentado para exigir mais arrasto)
        const superLikeThreshold = Math.abs(this.SUPERLIKE_POSITION) * 0.8; // 80% do movimento lateral (aumentado para exigir mais arrasto)
        
        if (dx > likeThreshold) this.commitDirection = 'right';
        else if (dx < -superLikeThreshold) this.commitDirection = 'left';
        else this.commitDirection = null;
        
        // Emite o progresso do drag para feedback visual
        // Calcula progresso baseado na posição final alvo (LIKE_POSITION ou SUPERLIKE_POSITION)
        const maxDistance = dx > 0 ? this.LIKE_POSITION : Math.abs(this.SUPERLIKE_POSITION);
        const progress = Math.min(Math.abs(dx) / maxDistance, 1);
        const direction = dx > 0 ? 'right' : dx < 0 ? 'left' : null;
        this.dragProgress.emit({ direction, progress });
      } else if (isVerticalDrag) {
        // Vertical drag for hide - só funciona se não houver intenção horizontal e estiver no centro
        // Ajusta o startX para o centro atual da bolinha para evitar desvio horizontal
        if (!this.verticalDragStartXAdjusted) {
          // Calcula a posição X atual do centro da bolinha
          const currentCenterX = event.clientX - this.translateX;
          // Ajusta startX para que o cálculo de dx seja sempre relativo ao centro
          this.startX = currentCenterX;
          this.verticalDragStartXAdjusted = true;
        }
        
        this.translateY = Math.min(dy, this.HIDE_THRESHOLD);
        this.translateX = 0; // Garante que não há movimento horizontal durante arraste vertical
        this.visualDirection = null;
        this.actionIconOpacity = 0;

        // Determine if hide should be triggered
        if (dy > this.HIDE_THRESHOLD) {
          this.commitDirection = 'down';
        } else {
          this.commitDirection = null;
        }
        
        // Emite o progresso do drag para feedback visual
        const progress = Math.min(dy / this.HIDE_THRESHOLD, 1);
        this.dragProgress.emit({ direction: 'down', progress });
      }
    });
  }

  /**
   * Finaliza a ação de arrastar, definindo o novo estado com base na
   * direção e distância percorridas.
   */
  private onHoldEnd(): void {
    if (!this.isHolding) return;
    this.dragEnd.emit();
    
    if (this.commitDirection === 'down' && this.enableHide) {
      this.hideTriggered.emit();
      this.translateY = 0;
    } else {
      const prevState = this.currentState;
      switch (this.commitDirection) {
        case 'left':
          if (prevState !== SliderState.SUPERLIKE) {
            this.setState(SliderState.SUPERLIKE);
          } else {
            this.setState(SliderState.SUPERLIKE, false);
          }
          break;
          
        case 'right':
          if (prevState !== SliderState.LIKE) {
            this.setState(SliderState.LIKE);
          } else {
            this.setState(SliderState.LIKE, false);
          }
          break;
        default:
          this.setState(prevState, false);
          break;
      }
    }

    this.isHolding = false;
    this.commitDirection = null;
    this.translateY = 0;
    this.hasHorizontalIntent = false; // Reset da flag ao terminar o arraste
    this.verticalDragStartXAdjusted = false; // Reset da flag ao terminar o arraste
    this.removeEventListeners();
  }

  /**
   * Define o estado visual e lógico do componente.
   * @param newState O novo estado a ser aplicado (IDLE, LIKE, SUPERLIKE).
   * @param emitEvent Se verdadeiro, emite o evento 'stateChange' para o componente pai.
   */
  public setState(newState: SliderState, emitEvent = true): void {
    this.currentState = newState;
    
    // Garante que as posições foram calculadas antes de aplicar o estado
    // Se ainda não foram calculadas, força o cálculo
    if ((this.LIKE_POSITION === 120 && this.SUPERLIKE_POSITION === -120) && 
        this.sliderBarRef?.nativeElement && 
        this.sliderHandleRef?.nativeElement) {
      this.calculatePositions();
    }
    
    switch (newState) {
      case SliderState.SUPERLIKE:
        // Garante que a posição foi calculada
        if (this.SUPERLIKE_POSITION === -120 && this.sliderBarRef?.nativeElement && this.sliderHandleRef?.nativeElement) {
          this.calculatePositions();
        }
        this.translateX = this.SUPERLIKE_POSITION;
        this.actionIconOpacity = 1;
        this.visualDirection = 'left';
        this.stop1_pos = 0; this.stop2_pos = 90; this.stop3_pos = 140;
        break;
      case SliderState.LIKE:
        // Garante que a posição foi calculada
        if (this.LIKE_POSITION === 120 && this.sliderBarRef?.nativeElement && this.sliderHandleRef?.nativeElement) {
          this.calculatePositions();
        }
        this.translateX = this.LIKE_POSITION;
        this.actionIconOpacity = 1;
        this.visualDirection = 'right';
        this.stop1_pos = -40; this.stop2_pos = 10; this.stop3_pos = 100;
        break;
      default: // IDLE
        this.translateX = 0;
        this.actionIconOpacity = 0;
        this.visualDirection = null;
        this.stop1_pos = 0; this.stop2_pos = 50; this.stop3_pos = 100;
        break;
    }

    // Força a detecção de mudanças para garantir que o DOM seja atualizado
    this.cdr.detectChanges();
    
    // Força a atualização do DOM dentro da zona do Angular
    this.zone.run(() => {
      // Força uma nova aplicação após um frame para garantir que o DOM foi atualizado
      requestAnimationFrame(() => {
        // Re-aplica o translateX para garantir que está correto
        if (newState === SliderState.SUPERLIKE) {
          this.translateX = this.SUPERLIKE_POSITION;
          this.actionIconOpacity = 1;
          this.visualDirection = 'left';
          
          // Aplica diretamente no elemento DOM se necessário
          if (this.sliderHandleRef?.nativeElement) {
            const element = this.sliderHandleRef.nativeElement;
            element.style.transform = `translate(-50%, -50%) translateX(${this.SUPERLIKE_POSITION}px) translateY(0px)`;
          }
        } else if (newState === SliderState.LIKE) {
          this.translateX = this.LIKE_POSITION;
          this.actionIconOpacity = 1;
          this.visualDirection = 'right';
          
          // Aplica diretamente no elemento DOM se necessário
          if (this.sliderHandleRef?.nativeElement) {
            const element = this.sliderHandleRef.nativeElement;
            element.style.transform = `translate(-50%, -50%) translateX(${this.LIKE_POSITION}px) translateY(0px)`;
          }
        }
        // Força novamente a detecção de mudanças após atualizar
        this.cdr.detectChanges();
      });
    });

    if (emitEvent) {
        this.stateChange.emit(this.currentState);
      }
    }
    
  public setExternalDrag(dx: number): void {
    if (this.isLocked()) {
      return;
    }

    this.zone.run(() => {
      const constrainedX = Math.max(
        this.SUPERLIKE_POSITION,
        Math.min(dx, this.LIKE_POSITION)
      );

      this.translateX = constrainedX;
      this.translateY = 0;

      this.updateVisualState(constrainedX);
    });
  }

  private updateVisualState(position: number): void {
    const maxDistance = Math.max(
      Math.abs(this.LIKE_POSITION),
      Math.abs(this.SUPERLIKE_POSITION)
    );

    const travelPercent = Math.min(Math.abs(position) / maxDistance, 1);
    this.actionIconOpacity = travelPercent;

    if (position > 5) {
      this.visualDirection = 'right';
    } else if (position < -5) {
      this.visualDirection = 'left';
    } else {
      this.visualDirection = null;
    }

    const flex = 40;
    const progress = position / this.LIKE_POSITION;
    this.stop2_pos = 50 - (progress * flex);
    this.stop1_pos = Math.min(0, 0 - (progress * flex));
    this.stop3_pos = Math.max(100, 100 - (progress * flex));
  }
  public getLikePosition(): number {
    return this.LIKE_POSITION;
  }

  public getSuperLikePosition(): number {
    return this.SUPERLIKE_POSITION;
  }

  /**
   * Força o reset completo do slider, recalculando posições e resetando para IDLE
   * Útil quando o componente é reutilizado ou inicializado pela primeira vez
   */
  public forceReset(): void {
    this.zone.run(() => {
      // Recalcula as posições
      this.calculatePositions();
      
      // Reseta para IDLE
      this.currentState = SliderState.IDLE;
      this.translateX = 0;
      this.translateY = 0;
      this.actionIconOpacity = 0;
      this.visualDirection = null;
      this.stop1_pos = 0;
      this.stop2_pos = 50;
      this.stop3_pos = 100;
      this.isHolding = false;
      this.hasHorizontalIntent = false;
      this.verticalDragStartXAdjusted = false;
      this.commitDirection = null;
    });
  }

  /**
   * Reseta o componente para o estado inicial IDLE.
   */
  public reset(): void {
    this.setState(SliderState.IDLE, false);
  }

  private removeEventListeners(): void {
    document.removeEventListener('pointermove', this.onDragFn);
    document.removeEventListener('pointerup', this.onHoldEndFn);
    document.removeEventListener('pointercancel', this.onHoldEndFn);
  }
}